import { useCallback } from 'react';

import * as _Popover from '@radix-ui/react-popover';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { SlidersHorizontalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ToggleButton } from '@/components/ui/custom/ToggleButton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

type PopoverContentProps = React.ComponentProps<typeof _Popover.PopoverContent>;

interface ColumnVisibilityMenuProps<TData = unknown> {
  columns: ColumnDef<TData>[];
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  onReset: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInteractOutside?: PopoverContentProps['onInteractOutside'];
}

export const ColumnVisibilityMenu = <TData = unknown,>({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  onReset,
  open,
  onOpenChange,
  onInteractOutside,
}: ColumnVisibilityMenuProps<TData>) => {
  const { t } = useTranslation();

  const handleToggleColumn = useCallback(
    (columnId: string, currentVisibility: boolean) => {
      onColumnVisibilityChange({ ...columnVisibility, [columnId]: !currentVisibility });
    },
    [onColumnVisibilityChange, columnVisibility]
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline-neutral" size="sm" aria-label={t('common.customize_columns')}>
          <SlidersHorizontalIcon />
          {t('common.customize')}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-auto max-w-xs p-0"
        onInteractOutside={onInteractOutside}
      >
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm leading-6 font-semibold">{t('common.toggle_columns')}</span>
          <Button
            variant="link"
            onClick={onReset}
            className="h-auto min-w-0 p-0 text-accent hover:no-underline"
          >
            {t('common.reset')}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 px-3 pt-1 pb-4">
          {columns.map((column) => {
            const id = column.id ?? '';
            const rawLabel = column.meta?.label ?? id;
            // Only translate if it looks like a translation key (contains a dot)
            const label = rawLabel.includes('.') ? t(rawLabel) : rawLabel;
            const isVisible = columnVisibility[id] ?? true;

            return (
              <ToggleButton
                key={id}
                pressed={isVisible}
                onClick={() => handleToggleColumn(id, isVisible)}
              >
                {label}
              </ToggleButton>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
